import apiClient from './client';

/**
 * Dashboard API functions
 * Handles all dashboard-related API calls
 */

/**
 * Get combined dashboard data (dashboard + quick actions + activity feed) in one API call
 * This reduces HTTP requests from 3 to 1, improving performance significantly
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date for filtering (optional)
 * @param {string} params.end_date - End date for filtering (optional)
 * @returns {Promise} Combined dashboard data { dashboard, quickActions, activityFeed }
 */
export const getCombinedDashboard = async (params = {}) => {
  const response = await apiClient.get('/dashboard/combined', { params });
  return response.data;
};

/**
 * Get Super Admin dashboard data
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date for filtering (optional)
 * @param {string} params.end_date - End date for filtering (optional)
 * @returns {Promise} Dashboard data
 */
export const getSuperAdminDashboard = async (params = {}) => {
  const response = await apiClient.get('/dashboard/superadmin', { params });
  return response.data;
};

/**
 * Get Admin dashboard data
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date for filtering (optional)
 * @param {string} params.end_date - End date for filtering (optional)
 * @returns {Promise} Dashboard data
 */
export const getAdminDashboard = async (params = {}) => {
  const response = await apiClient.get('/dashboard/admin', { params });
  return response.data;
};

/**
 * Get Staff dashboard data
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date for filtering (optional)
 * @param {string} params.end_date - End date for filtering (optional)
 * @returns {Promise} Dashboard data
 */
export const getStaffDashboard = async (params = {}) => {
  const response = await apiClient.get('/dashboard/staff', { params });
  return response.data;
};

/**
 * Get activity feed
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of activities to fetch (default: 20)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @param {string} params.activity_type - Filter by activity type (optional)
 * @param {string} params.start_date - Start date for filtering (optional)
 * @param {string} params.end_date - End date for filtering (optional)
 * @returns {Promise} Activity feed data
 */
export const getActivityFeed = async (params = {}) => {
  const response = await apiClient.get('/dashboard/activity-feed', { params });
  return response.data;
};

/**
 * Get quick actions based on user role
 * @returns {Promise} Quick actions list
 */
export const getQuickActions = async () => {
  const response = await apiClient.get('/dashboard/quick-actions');
  return response.data;
};

/**
 * Get user notifications
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of notifications to fetch (default: 20)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @param {boolean} params.unread_only - Filter only unread notifications (optional)
 * @param {string} params.notification_type - Filter by notification type (optional)
 * @returns {Promise} Notifications data
 */
export const getNotifications = async (params = {}) => {
  const response = await apiClient.get('/notifications', { params });
  return response.data;
};

/**
 * Get unread notification count
 * @returns {Promise} Unread count
 */
export const getNotificationCount = async () => {
  const response = await apiClient.get('/notifications/count');
  return response.data;
};

/**
 * Mark notification as read
 * @param {number} notificationId - Notification ID
 * @returns {Promise} Success response
 */
export const markNotificationAsRead = async (notificationId) => {
  const response = await apiClient.patch(`/notifications/${notificationId}/read`);
  return response.data;
};

/**
 * Mark all notifications as read
 * @returns {Promise} Success response
 */
export const markAllNotificationsAsRead = async () => {
  const response = await apiClient.patch('/notifications/read-all');
  return response.data;
};

/**
 * Delete notification
 * @param {number} notificationId - Notification ID
 * @returns {Promise} Success response
 */
export const deleteNotification = async (notificationId) => {
  const response = await apiClient.delete(`/notifications/${notificationId}`);
  return response.data;
};

export default {
  getCombinedDashboard,
  getSuperAdminDashboard,
  getAdminDashboard,
  getStaffDashboard,
  getActivityFeed,
  getQuickActions,
  getNotifications,
  getNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
};
